import React, { useState, useEffect, useRef } from 'react';
import { getBackendUrl } from '@/config';
import { DashboardLayout } from '@/components/layout/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { User, Mail, Phone, Save, Upload, Camera, CheckCircle, AlertCircle, Loader2, ShieldCheck, Copy, Check, FlaskConical } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '@/contexts/AuthContext';
import axios from 'axios';
import {
  validateName,
  validatePhoneNumber,
  handleNumericInput,
  handleAlphabeticInput,
} from '@/utils/validators';

const API_URL = getBackendUrl();

// Helper to format phone for display (remove +1 prefix)
const formatPhoneForDisplay = (phone) => {
  if (!phone) return '';
  // Remove +1 prefix if present
  let cleaned = phone.replace(/^\+1/, '');
  // Remove any non-digit characters
  cleaned = cleaned.replace(/\D/g, '');
  return cleaned;
};

// Helper to format phone for API (add +1 prefix)
const formatPhoneForAPI = (phone) => {
  if (!phone) return '';
  const cleaned = phone.replace(/\D/g, '');
  if (cleaned.length === 10) {
    return `+1${cleaned}`;
  }
  return phone;
};

export default function ProfilePage() {
  const { user, updateUser } = useAuth();
  const fileInputRef = useRef(null);
  const otpInputRefs = useRef([]);
  const [profileImage, setProfileImage] = useState(null);
  const [imagePreview, setImagePreview] = useState('');
  const [saving, setSaving] = useState(false);
  const [validationErrors, setValidationErrors] = useState({});
  
  // Email verification state
  const [showEmailVerifyDialog, setShowEmailVerifyDialog] = useState(false);
  const [emailVerifyStep, setEmailVerifyStep] = useState('email'); // 'email' | 'otp'
  const [verifyEmail, setVerifyEmail] = useState('');
  const [emailOtp, setEmailOtp] = useState(['', '', '', '', '', '']);
  const [emailVerifyLoading, setEmailVerifyLoading] = useState(false);
  const [emailVerifyError, setEmailVerifyError] = useState('');
  const [testMode, setTestMode] = useState(false);
  const [testOtp, setTestOtp] = useState('');
  const [copied, setCopied] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(0);
  
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
  });

  // Resend cooldown timer
  useEffect(() => {
    if (resendCooldown > 0) {
      const timer = setTimeout(() => setResendCooldown(resendCooldown - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [resendCooldown]);

  // Fetch fresh user data on page load (run only once)
  useEffect(() => {
    let isMounted = true;
    
    const fetchUserData = async () => {
      try {
        const token = localStorage.getItem('access_token');
        if (!token) return;
        
        const response = await axios.get(
          `${API_URL}/api/auth/me`,
          { headers: { 'Authorization': `Bearer ${token}` } }
        );
        
        if (isMounted) {
          console.log('[ProfilePage] Fresh user data:', response.data);
          updateUser(response.data);
        }
      } catch (error) {
        console.error('[ProfilePage] Error fetching user data:', error);
      }
    };
    
    fetchUserData();
    
    return () => {
      isMounted = false;
    };
  }, [updateUser]);
  
  // Update form data when user changes (with stable reference check)
  useEffect(() => {
    if (user) {
      setFormData(prev => {
        const newData = {
          firstName: user.first_name || '',
          lastName: user.last_name || '',
          email: user.email || '',
          phone: formatPhoneForDisplay(user.phone) || '',
        };
        
        // Only update if data actually changed to prevent loops
        if (
          prev.firstName === newData.firstName &&
          prev.lastName === newData.lastName &&
          prev.email === newData.email &&
          prev.phone === newData.phone
        ) {
          return prev;
        }
        
        return newData;
      });
      
      // Update image preview from user profile
      if (user.profile_image && user.profile_image.length > 0) {
        const backendUrl = getBackendUrl();
        const imageUrl = user.profile_image.startsWith('/') 
          ? `${backendUrl}${user.profile_image}`
          : user.profile_image;
        setImagePreview(prev => prev !== imageUrl ? imageUrl : prev);
      } else {
        setImagePreview(prev => prev !== '' ? '' : prev);
      }
    }
  }, [user]);

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validate file type
    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      toast.error('Please upload a valid image (JPG, PNG, or WEBP)');
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Image size must be less than 5MB');
      return;
    }

    // Store the file for upload
    setProfileImage(file);
    
    // Create preview immediately
    const reader = new FileReader();
    reader.onloadend = () => {
      setImagePreview(reader.result);
      console.log('Image preview set:', reader.result.substring(0, 50));
    };
    reader.onerror = () => {
      toast.error('Failed to read image file');
      console.error('FileReader error');
    };
    reader.readAsDataURL(file);
    
    toast.success('Image selected. Click Save Changes to upload.');
  };

  const validateForm = () => {
    const errors = {};

    // First Name validation - optional for phone-first users
    if (formData.firstName && formData.firstName.trim() !== '') {
      const firstNameValidation = validateName(formData.firstName);
      if (!firstNameValidation.isValid) {
        errors.firstName = firstNameValidation.error;
      }
    }

    // Last Name validation - optional for phone-first users
    if (formData.lastName && formData.lastName.trim() !== '') {
      const lastNameValidation = validateName(formData.lastName);
      if (!lastNameValidation.isValid) {
        errors.lastName = lastNameValidation.error;
      }
    }

    // Phone Number validation (optional but must be valid if provided)
    if (formData.phone && formData.phone.trim() !== '') {
      const phoneValidation = validatePhoneNumber(formData.phone);
      if (!phoneValidation.isValid) {
        errors.phone = phoneValidation.error;
      }
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSave = async () => {
    // Validate form
    if (!validateForm()) {
      toast.error('Please fix all validation errors before saving');
      return;
    }

    try {
      setSaving(true);
      const backendUrl = getBackendUrl();
      const token = localStorage.getItem('access_token');

      // Prepare form data for multipart upload if image exists
      const submitData = new FormData();
      submitData.append('first_name', formData.firstName);
      submitData.append('last_name', formData.lastName);
      // Format phone with +1 prefix for API
      submitData.append('phone', formatPhoneForAPI(formData.phone));
      
      // Include email if provided (for linking email to phone-only accounts)
      if (formData.email && formData.email.trim() !== '') {
        submitData.append('email', formData.email.trim());
      }
      
      if (profileImage) {
        submitData.append('profile_image', profileImage);
      }

      const response = await axios.put(
        `${backendUrl}/api/users/profile`,
        submitData,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'multipart/form-data'
          }
        }
      );

      console.log('[ProfilePage] Save response:', response.data);
      console.log('[ProfilePage] profile_image in response:', response.data.profile_image);
      
      // Update auth context with new user data (this will trigger useEffect)
      updateUser(response.data);
      
      toast.success('Profile updated successfully!');
      setProfileImage(null);
    } catch (error) {
      console.error('[ProfilePage] Error updating profile:', error);
      console.error('[ProfilePage] Error response:', error.response?.data);
      
      // If image upload failed specifically, show that error
      if (profileImage && error.response?.status === 413) {
        toast.error('Image file is too large. Please use an image under 5MB.');
      } else {
        toast.error(error.response?.data?.detail || 'Failed to update profile');
      }
    } finally {
      setSaving(false);
    }
  };

  // Email verification functions
  const handleStartEmailVerification = () => {
    setVerifyEmail(formData.email || '');
    setEmailVerifyStep('email');
    setEmailOtp(['', '', '', '', '', '']);
    setEmailVerifyError('');
    setTestMode(false);
    setTestOtp('');
    setShowEmailVerifyDialog(true);
  };

  const handleSendEmailOtp = async () => {
    if (!verifyEmail || !verifyEmail.includes('@')) {
      setEmailVerifyError('Please enter a valid email address');
      return;
    }

    setEmailVerifyLoading(true);
    setEmailVerifyError('');

    try {
      const token = localStorage.getItem('access_token');
      const response = await fetch(`${API_URL}/api/otp/email/send-verification`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ email: verifyEmail })
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.detail || 'Failed to send verification code');
      }

      setEmailVerifyStep('otp');
      setResendCooldown(60);
      toast.success('Verification code sent!');

      if (data.test_mode && data.test_otp) {
        setTestMode(true);
        setTestOtp(data.test_otp);
      }
    } catch (err) {
      setEmailVerifyError(err.message);
      toast.error(err.message);
    } finally {
      setEmailVerifyLoading(false);
    }
  };

  const handleVerifyEmailOtp = async () => {
    const otpCode = emailOtp.join('');
    if (otpCode.length !== 6) {
      setEmailVerifyError('Please enter the complete 6-digit code');
      return;
    }

    setEmailVerifyLoading(true);
    setEmailVerifyError('');

    try {
      const token = localStorage.getItem('access_token');
      const response = await fetch(`${API_URL}/api/otp/email/verify`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ email: verifyEmail, code: otpCode })
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.detail || 'Invalid verification code');
      }

      // Update user in context
      updateUser(data.user);
      setFormData(prev => ({ ...prev, email: data.user.email }));
      
      toast.success('Email verified successfully!');
      setShowEmailVerifyDialog(false);
    } catch (err) {
      setEmailVerifyError(err.message);
      toast.error(err.message);
    } finally {
      setEmailVerifyLoading(false);
    }
  };

  const handleEmailOtpChange = (index, value) => {
    if (value.length > 1) {
      const digits = value.replace(/\D/g, '').slice(0, 6).split('');
      const newOtp = [...emailOtp];
      digits.forEach((digit, i) => {
        if (index + i < 6) {
          newOtp[index + i] = digit;
        }
      });
      setEmailOtp(newOtp);
      const nextIndex = Math.min(index + digits.length, 5);
      otpInputRefs.current[nextIndex]?.focus();
    } else {
      const newOtp = [...emailOtp];
      newOtp[index] = value.replace(/\D/g, '');
      setEmailOtp(newOtp);
      if (value && index < 5) {
        otpInputRefs.current[index + 1]?.focus();
      }
    }
    setEmailVerifyError('');
  };

  const handleEmailOtpKeyDown = (index, e) => {
    if (e.key === 'Backspace' && !emailOtp[index] && index > 0) {
      otpInputRefs.current[index - 1]?.focus();
    }
  };

  const handleCopyOtp = () => {
    navigator.clipboard.writeText(testOtp);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
    toast.success('Code copied!');
  };

  const fullName = `${formData.firstName} ${formData.lastName}`.trim() || 'User';

  if (!user) {
    return <div>Loading...</div>;
  }

  return (
    <DashboardLayout role={user.role} title="Profile" user={user}>
      <div className="mx-auto max-w-3xl space-y-6">
        <div>
          <h2 className="text-2xl font-bold text-foreground">My Profile</h2>
          <p className="mt-1 text-muted-foreground">Manage your account information</p>
        </div>

        {/* Profile Picture */}
        <Card className="card-elevated">
          <CardContent className="pt-6">
            <div className="flex items-center gap-6">
              <div className="relative">
                {imagePreview && imagePreview.length > 0 ? (
                  <div className="h-24 w-24 rounded-full overflow-hidden">
                    <img 
                      src={imagePreview} 
                      alt={fullName}
                      className="h-full w-full object-cover"
                      onError={(e) => {
                        console.error('Image failed to load:', imagePreview);
                        setImagePreview('');
                      }}
                    />
                  </div>
                ) : (
                  <div className="h-24 w-24 rounded-full overflow-hidden bg-primary flex items-center justify-center">
                    <span className="text-2xl text-primary-foreground font-semibold">
                      {fullName.split(' ').map(n => n[0]).join('').toUpperCase()}
                    </span>
                  </div>
                )}
                <button
                  onClick={() => fileInputRef.current?.click()}
                  className="absolute bottom-0 right-0 rounded-full bg-primary p-2 text-primary-foreground shadow-lg hover:bg-primary/90 transition-colors"
                >
                  <Camera className="h-4 w-4" />
                </button>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/jpeg,image/jpg,image/png,image/webp"
                  onChange={handleImageChange}
                  className="hidden"
                />
              </div>
              <div>
                <h3 className="text-xl font-semibold">{fullName}</h3>
                <p className="text-sm text-muted-foreground">{formData.email}</p>
                <Button 
                  variant="outline" 
                  size="sm" 
                  className="mt-2 gap-2"
                  onClick={() => fileInputRef.current?.click()}
                >
                  <Upload className="h-4 w-4" />
                  Change Picture
                </Button>
                <p className="text-xs text-muted-foreground mt-1">
                  JPG, PNG or WEBP. Max 5MB.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Personal Information */}
        <Card className="card-elevated">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <User className="h-5 w-5" />
              Personal Information
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid gap-4 md:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="firstName">First Name</Label>
                <Input
                  id="firstName"
                  value={formData.firstName}
                  onChange={(e) => setFormData({ ...formData, firstName: handleAlphabeticInput(e.target.value) })}
                  placeholder="John"
                />
                {validationErrors.firstName && (
                  <p className="text-xs text-red-500">{validationErrors.firstName}</p>
                )}
              </div>
              <div className="space-y-2">
                <Label htmlFor="lastName">Last Name</Label>
                <Input
                  id="lastName"
                  value={formData.lastName}
                  onChange={(e) => setFormData({ ...formData, lastName: handleAlphabeticInput(e.target.value) })}
                  placeholder="Doe"
                />
                {validationErrors.lastName && (
                  <p className="text-xs text-red-500">{validationErrors.lastName}</p>
                )}
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              {user.email && user.email.trim() !== '' ? (
                <>
                  <div className="flex items-center gap-2">
                    <Input
                      id="email"
                      type="email"
                      value={formData.email}
                      disabled
                      className="bg-muted flex-1"
                    />
                    {user.email_verified === true ? (
                      <span className="flex items-center gap-1 text-xs text-green-600">
                        <CheckCircle className="h-3 w-3" />
                        Verified
                      </span>
                    ) : (
                      <Button 
                        size="sm" 
                        variant="outline"
                        onClick={handleStartEmailVerification}
                        className="text-xs"
                      >
                        Verify Email
                      </Button>
                    )}
                  </div>
                  <p className="text-xs text-muted-foreground">
                    {user.email_verified === true 
                      ? "Email cannot be changed once verified" 
                      : "Please verify your email to enable notifications"}
                  </p>
                </>
              ) : (
                <>
                  <div className="flex items-center gap-2">
                    <Input
                      id="email"
                      type="email"
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      placeholder="your@email.com"
                      className="flex-1"
                    />
                    {formData.email && formData.email.includes('@') && (
                      <Button 
                        size="sm" 
                        variant="outline"
                        onClick={handleStartEmailVerification}
                        className="text-xs whitespace-nowrap"
                      >
                        Add & Verify
                      </Button>
                    )}
                  </div>
                  <p className="text-xs text-muted-foreground">Add your email for account recovery and notifications</p>
                </>
              )}
            </div>
            <div className="space-y-2">
              <Label htmlFor="phone">Phone Number</Label>
              {user.phone ? (
                <>
                  <div className="flex items-center gap-2">
                    <div className="relative flex-1">
                      <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground text-sm">+1</span>
                      <Input
                        id="phone"
                        type="tel"
                        value={formData.phone}
                        disabled
                        className="bg-muted pl-10"
                      />
                    </div>
                    {user.phone_verified ? (
                      <span className="flex items-center gap-1 text-xs text-green-600 whitespace-nowrap">
                        <CheckCircle className="h-3 w-3" />
                        Verified
                      </span>
                    ) : (
                      <span className="flex items-center gap-1 text-xs text-amber-600 whitespace-nowrap">
                        <AlertCircle className="h-3 w-3" />
                        Not verified
                      </span>
                    )}
                  </div>
                  <p className="text-xs text-muted-foreground">Phone number cannot be changed once set</p>
                </>
              ) : (
                <>
                  <div className="flex items-center gap-2">
                    <div className="relative flex-1">
                      <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground text-sm">+1</span>
                      <Input
                        id="phone"
                        type="tel"
                        inputMode="numeric"
                        pattern="[0-9]*"
                        value={formData.phone}
                        onChange={(e) => setFormData({ ...formData, phone: handleNumericInput(e.target.value, 10) })}
                        onKeyPress={(e) => {
                          if (!/[0-9]/.test(e.key) && !['Backspace', 'Delete', 'ArrowLeft', 'ArrowRight', 'Tab'].includes(e.key)) {
                            e.preventDefault();
                          }
                        }}
                        onPaste={(e) => {
                          e.preventDefault();
                          const pastedText = e.clipboardData.getData('text');
                          const cleaned = handleNumericInput(pastedText, 10);
                          setFormData({ ...formData, phone: cleaned });
                        }}
                        placeholder="5551234567"
                        maxLength={10}
                        autoComplete="tel"
                        className="pl-10"
                      />
                    </div>
                  </div>
                  <p className="text-xs text-muted-foreground">Add your phone number for SMS notifications</p>
                  {validationErrors.phone && (
                    <p className="text-xs text-red-500">{validationErrors.phone}</p>
                  )}
                </>
              )}
            </div>
          </CardContent>
        </Card>

        <div className="flex justify-end">
          <Button onClick={handleSave} className="gap-2" disabled={saving}>
            <Save className="h-4 w-4" />
            {saving ? 'Saving...' : 'Save Changes'}
          </Button>
        </div>
      </div>

      {/* Email Verification Dialog */}
      <Dialog open={showEmailVerifyDialog} onOpenChange={setShowEmailVerifyDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Mail className="h-5 w-5" />
              {emailVerifyStep === 'email' ? 'Verify Email' : 'Enter Verification Code'}
            </DialogTitle>
            <DialogDescription>
              {emailVerifyStep === 'email' 
                ? 'We\'ll send a verification code to your email'
                : `Enter the 6-digit code sent to ${verifyEmail}`}
            </DialogDescription>
          </DialogHeader>

          {emailVerifyStep === 'email' ? (
            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="verify-email">Email Address</Label>
                <Input
                  id="verify-email"
                  type="email"
                  value={verifyEmail}
                  onChange={(e) => setVerifyEmail(e.target.value)}
                  placeholder="your@email.com"
                  disabled={emailVerifyLoading}
                />
              </div>

              {emailVerifyError && (
                <Alert variant="destructive">
                  <AlertDescription>{emailVerifyError}</AlertDescription>
                </Alert>
              )}

              <Button 
                onClick={handleSendEmailOtp} 
                className="w-full" 
                disabled={emailVerifyLoading || !verifyEmail}
              >
                {emailVerifyLoading ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Sending...
                  </>
                ) : (
                  'Send Verification Code'
                )}
              </Button>

              <div className="flex items-center gap-2 text-xs text-muted-foreground justify-center">
                <ShieldCheck className="h-3.5 w-3.5" />
                Your data is protected with encryption
              </div>
            </div>
          ) : (
            <div className="space-y-4">
              {/* Test Mode Banner */}
              {testMode && (
                <Alert className="bg-amber-50 border-amber-200 dark:bg-amber-900/20 dark:border-amber-800">
                  <FlaskConical className="h-4 w-4 text-amber-600" />
                  <AlertDescription className="text-amber-800 dark:text-amber-200">
                    <div className="flex items-center justify-between">
                      <span>Test mode: <strong>{testOtp}</strong></span>
                      <Button 
                        type="button"
                        size="sm" 
                        variant="outline"
                        className="h-7 px-2 border-amber-300"
                        onClick={handleCopyOtp}
                      >
                        {copied ? <Check className="h-3 w-3" /> : <Copy className="h-3 w-3" />}
                      </Button>
                    </div>
                  </AlertDescription>
                </Alert>
              )}

              {/* OTP Input */}
              <div className="flex justify-center gap-2">
                {emailOtp.map((digit, index) => (
                  <Input
                    key={index}
                    ref={(el) => (otpInputRefs.current[index] = el)}
                    type="text"
                    inputMode="numeric"
                    maxLength={6}
                    value={digit}
                    onChange={(e) => handleEmailOtpChange(index, e.target.value)}
                    onKeyDown={(e) => handleEmailOtpKeyDown(index, e)}
                    className="w-12 h-12 text-center text-lg font-semibold"
                    disabled={emailVerifyLoading}
                    autoFocus={index === 0}
                  />
                ))}
              </div>

              {emailVerifyError && (
                <Alert variant="destructive">
                  <AlertDescription>{emailVerifyError}</AlertDescription>
                </Alert>
              )}

              <Button 
                onClick={handleVerifyEmailOtp} 
                className="w-full" 
                disabled={emailVerifyLoading || emailOtp.join('').length !== 6}
              >
                {emailVerifyLoading ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Verifying...
                  </>
                ) : (
                  'Verify Email'
                )}
              </Button>

              <div className="flex items-center justify-between text-sm">
                <button
                  type="button"
                  onClick={() => {
                    setEmailVerifyStep('email');
                    setEmailOtp(['', '', '', '', '', '']);
                    setEmailVerifyError('');
                    setTestMode(false);
                    setTestOtp('');
                  }}
                  className="text-muted-foreground hover:text-foreground"
                >
                  Change email
                </button>
                
                <button
                  type="button"
                  onClick={handleSendEmailOtp}
                  disabled={resendCooldown > 0 || emailVerifyLoading}
                  className={resendCooldown > 0 ? 'text-muted-foreground' : 'text-primary hover:underline'}
                >
                  {resendCooldown > 0 ? `Resend in ${resendCooldown}s` : 'Resend code'}
                </button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
}
